import React from 'react';

import {EChannelType, EContentType, TChannelType, TItemId} from 'src/constants';
import {TMessageMatcher} from 'src/containers/MessageMatchers/definitions';

import {MaxContent} from './MaxContent';
import {PreviewText} from './PreviewText';
import {PushContent} from './PushContent';
import {ViberContent} from './ViberContent';
import {VkNotifyContent} from './VkNotifyContent';
import {WhatsAppContent} from './WhatsAppContent';
import * as S from './style';
import {FOOTER_TIME} from './utils';

export type TMessagePreviewPlacement = 'right' | 'left' | 'center';

type TProps = {
  channelType: TChannelType;
  content?: TMessageMatcher['content'];
  className?: string;
  placement?: TMessagePreviewPlacement;
  text?: string;
  parentText?: string;
  contentType?: TMessageMatcher['contentType'];
  type?: TMessageMatcher['type'];
  language?: TMessageMatcher['language'];
  tenantId?: TItemId;
  variablesPath?: string;
  noActions?: boolean;
};

const TextContent = React.memo<{children: React.ReactNode}>(({children}) => (
  <S.Message isInline>
    {children}
    <S.Time>
      <span>{FOOTER_TIME}</span>
    </S.Time>
  </S.Message>
));

export const MessagePreview = React.memo<TProps>(
  ({
    className,
    channelType,
    language,
    placement = 'right',
    contentType = EContentType.TEXT,
    parentText,
    type,
    content = {},
    tenantId,
    noActions,
    variablesPath,
  }) => {
    const formattedText = React.useMemo(
      () =>
        content.text ? (
          <PreviewText
            text={content.text}
            channelType={channelType}
            type={type}
            parentText={parentText}
            tenantId={tenantId}
            variablesPath={variablesPath}
          />
        ) : null,
      [content.text, channelType, type, parentText],
    );

    if (channelType === EChannelType.PUSH) {
      return (
        <S.PreviewWrapper
          placement={placement}
          width="320px"
          className={className}
          {...edna.renderTestId(edna.TEST_IDS.PHONE_PREVIEW.CONTAINER)}
        >
          <PushContent content={content}>{formattedText}</PushContent>
        </S.PreviewWrapper>
      );
    }

    if (channelType === EChannelType.WHATSAPP) {
      return (
        <S.PreviewWrapper
          placement={placement}
          className={className}
          {...edna.renderTestId(edna.TEST_IDS.PHONE_PREVIEW.CONTAINER)}
        >
          <WhatsAppContent language={language} content={content} noActions={noActions}>
            {formattedText}
          </WhatsAppContent>
        </S.PreviewWrapper>
      );
    }

    return (
      <S.PreviewWrapper
        placement={placement}
        className={className}
        {...edna.renderTestId(edna.TEST_IDS.PHONE_PREVIEW.CONTAINER)}
      >
        <S.MessageWrapper>
          {[EChannelType.SMS, EChannelType.OK_NOTIFY].includes(channelType as EChannelType) && (
            <TextContent>{formattedText}</TextContent>
          )}
          {channelType === EChannelType.VK_NOTIFY && (
            <VkNotifyContent content={content}>{formattedText}</VkNotifyContent>
          )}
          {[EChannelType.MAX_BOT, EChannelType.MAX].includes(channelType as EChannelType) && (
            <MaxContent contentType={contentType} content={content}>
              {formattedText}
            </MaxContent>
          )}
          {channelType === EChannelType.VIBER && (
            <ViberContent contentType={contentType} content={content}>
              {formattedText}
            </ViberContent>
          )}
        </S.MessageWrapper>
      </S.PreviewWrapper>
    );
  },
);

MessagePreview.displayName = 'MessagePreview';
